import { deviceLocal } from '@/privacy/deviceStorage';
/**
 * Whether this reader has one of the sidebar's folding groups open — Views (F-0363) or Pages
 * (I-1389) — under one work area.
 *
 * Per work area and per browser, the same reasoning `pageTreeExpansion.ts` already carries for a
 * work area's page tree: the group's contents are the work area's, unchanged by who is looking, but
 * whether this reader has it open is theirs. `localStorage` is the store, so it survives a reload;
 * the key carries the group and the work area's id, so folding Pages folds nothing else, and
 * opening a group in one work area opens nothing in another.
 *
 * ⭐ **With no stored state a group is open.** Both groups were always open before they could fold:
 * defaulting one closed on the day it ships would hide something every existing reader expects to
 * find there.
 *
 * Every access is wrapped: a `localStorage` that throws or is blocked (a private window, site data
 * turned off) reads as "open", and a write that fails simply does not remember — nothing here is
 * load-bearing for drawing the sidebar, and nothing here reaches the screen as an error.
 */

/**
 * Each group's key prefix. Views keeps the key it has had since F-0363, so a reader's stored choice
 * survives this module taking it over.
 */
const KEY_PREFIXES = {
  views: 'featit:viewListExpanded:',
  pages: 'featit:pagesGroupExpanded:',
} as const;

export type SidebarGroupType = keyof typeof KEY_PREFIXES;

/** The key for one group in one work area. Keyed globally, two work areas would share one. */
export function sidebarGroupExpansionKey(group: SidebarGroupType, workAreaId: string): string {
  return `${KEY_PREFIXES[group]}${workAreaId}`;
}

function store(): Storage | null {
  try {
    // Reading the property is itself what throws in a browser with site data blocked.
    return typeof window === 'undefined' ? null : deviceLocal;
  } catch {
    return null;
  }
}

/** Whether the group is open for this work area. Anything unreadable reads as open. */
export function readSidebarGroupExpanded(group: SidebarGroupType, workAreaId: string): boolean {
  try {
    const raw = store()?.getItem(sidebarGroupExpansionKey(group, workAreaId));
    if (raw === null || raw === undefined) return true;
    return raw === 'true';
  } catch {
    return true;
  }
}

/** Remembers whether the group is open. A store that refuses the write simply does not remember. */
export function writeSidebarGroupExpanded(
  group: SidebarGroupType,
  workAreaId: string,
  expanded: boolean,
): void {
  try {
    store()?.setItem(sidebarGroupExpansionKey(group, workAreaId), expanded ? 'true' : 'false');
  } catch {
    // A full or read-only store costs this reader the memory of whether the group was open, and
    // nothing else. It must never reach the screen as an error.
  }
}
